'use client';

import { roundToTwoDecimalPlaces } from '@/helpers/utils/numberHelpers';
import { useAppSelector } from '@/store';

import CircularGauge from './CircularGauge';
import RetailComission from './RetailComission';

const RpbWidget = () => {
  const retailPowerBonusData =
    useAppSelector(state => state.slices.dashboard.dashboardData?.retailPowerBonusData) || {};

  const calculateRetailCommission = (npv: number, pv: number) => {
    if (npv >= 100 && pv >= 2000) {
      return 30;
    } else if (npv >= 100 && pv >= 1000) {
      return 25;
    } else if (npv >= 100 && pv >= 500) {
      return 20;
    }
    return 15;
  };

  const getRetailCommissionText = (retailCommission: number) => {
    if (retailCommission === 15) {
      return 'Keep On Working! You need $100 in New PV and $500 in PV to earn an additional 5% for a total of 20% on all PV';
    } else if (retailCommission === 20) {
      return 'Keep On Working! You need $100 in New PV and $1000 in PV to earn an additional 10% for a total of 25% on all PV';
    } else if (retailCommission === 25) {
      return 'Keep On Working! You need $100 in New PV and $2000 in PV to earn an additional 15% for a total of 30% on all PV';
    } else if (retailCommission === 30) {
      return 'Congratulations! You have earned a total of 30% on all PV.';
    }
    return '';
  };

  const retailCommission = calculateRetailCommission(retailPowerBonusData.currentNPV, retailPowerBonusData.currentPV);
  const retailCommissionText = getRetailCommissionText(retailCommission);

  return (
    <div className="v2-shadow-md v2-p-[24px] md:v2-p-[24px] sm:v2-p-4 xs:v2-p-4 v2-bg-white v2-rounded-xs">
      <div className="v2-pb-5 v2-border-b v2-border-[#E5E5E5] v2-mb-5">
        <h2 className="v2-text-[#333333] v2-font-sweet-sans-pro-bold v2-font-bold v2-text-xl v2-text-center">
          Retail Power Bonus
        </h2>
      </div>
      <div className="v2-mb-[20px]">
        <p className="v2-p-2 v2-bg-[#F9F9F9] v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-center v2-rounded-xs">
          {retailCommissionText}
        </p>
      </div>
      <div className=" v2-flex v2-gap-[12px] v2-flex-col">
        <div className="v2-flex v2-flex-row v2-gap-[12px]">
          <div className="v2-py-5 v2-px-[13px] v2-bg-[#F9F9F9] v2-rounded-xs v2-w-full v2-flex v2-flex-col v2-items-center md:v2-px-5">
            <h4 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-base v2-text-center v2-text-[#333333]">
              NPV
            </h4>
            <CircularGauge
              percentage={retailPowerBonusData.npvProgressBarPercent}
              value={retailPowerBonusData.currentNPV}
            />
            <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-center v2-text-primary">
              Total: ${roundToTwoDecimalPlaces(retailPowerBonusData.totalNPVRequired)}
            </p>
          </div>
          <div className="v2-py-5 v2-px-[13px] v2-bg-[#F9F9F9] v2-rounded-xs v2-w-full v2-flex v2-flex-col v2-items-center md:v2-px-5">
            <h4 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-base v2-text-center v2-text-[#333333]">
              PV
            </h4>
            <CircularGauge
              percentage={retailPowerBonusData.pvProgressBarPercent}
              value={retailPowerBonusData.currentPV}
            />
            <p className="v2-font-sweet-sans-pro v2-font-normal v2-text-base v2-text-center v2-text-primary">
              Total: ${roundToTwoDecimalPlaces(retailPowerBonusData.totalPVRequired)}
            </p>
          </div>
        </div>
        <div className="v2-p-5 v2-bg-[#F9F9F9] v2-rounded-xs v2-w-full v2-flex v2-flex-col v2-items-center">
          <h4 className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-base v2-text-center v2-text-[#333333]">
            Retail Commission
          </h4>
          <div>
            <RetailComission
              arcValues={[
                {
                  percentage: 15,
                  label: '',
                  color: retailCommission < 15 ? '#F9F9F9' : '#154735',
                  showBorder: retailCommission < 15,
                  ...(retailCommission < 15
                    ? { borderColor: '#EAECF0', borderWidth: 4, percentageTextColor: '##000', labelTextColor: '#000' }
                    : {}),
                },
                {
                  percentage: 20,
                  label: '500PV',
                  color: retailCommission < 20 ? '#F9F9F9' : '#154735',
                  showBorder: retailCommission < 20,
                  ...(retailCommission < 20
                    ? { borderColor: '#EAECF0', borderWidth: 4, percentageTextColor: '##000', labelTextColor: '#000' }
                    : {}),
                },
                {
                  percentage: 25,
                  label: '1000PV',
                  color: retailCommission < 25 ? '#F9F9F9' : '#154735',
                  showBorder: retailCommission < 25,
                  ...(retailCommission < 25
                    ? { borderColor: '#EAECF0', borderWidth: 4, percentageTextColor: '##000', labelTextColor: '#000' }
                    : {}),
                },
                {
                  percentage: 30,
                  label: '2000PV',
                  color: retailCommission < 30 ? '#F9F9F9' : '#154735',
                  showBorder: retailCommission < 30,
                  ...(retailCommission < 30
                    ? { borderColor: '#EAECF0', borderWidth: 4, percentageTextColor: '##000', labelTextColor: '#000' }
                    : {}),
                },
              ]}
              horizontalOffset={-45}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RpbWidget;
